import React, { useMemo } from 'react';

/**
 * DeadlineCountdown - Displays upcoming deadlines with countdown timers
 * Grouped by urgency: Today, This Week, Upcoming
 */
const DeadlineCountdown = ({ deadlines = [], onItemClick, loading = false }) => {
    const grouped = useMemo(() => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
        const weekEnd = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

        const groups = {
            overdue: [],
            today: [],
            thisWeek: [],
            upcoming: []
        };

        (deadlines || []).forEach(d => {
            if (!d.date) return;
            const date = new Date(d.date);

            if (date < today) {
                groups.overdue.push({ ...d, date });
            } else if (date < tomorrow) {
                groups.today.push({ ...d, date });
            } else if (date < weekEnd) {
                groups.thisWeek.push({ ...d, date });
            } else {
                groups.upcoming.push({ ...d, date });
            }
        });

        return groups;
    }, [deadlines]);

    const getTimeRemaining = (date) => {
        const now = new Date();
        const diff = date.getTime() - now.getTime();

        if (diff < 0) return 'Overdue';

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h`;
        return 'Soon';
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'university_application':
            case 'application':
                return '🎓';
            case 'visa':
            case 'visa_application':
                return '✈️';
            case 'document':
                return '📄';
            case 'task':
                return '✓';
            case 'agreement':
                return '📝';
            default:
                return '📌';
        }
    };

    const DeadlineItem = ({ item, urgency }) => (
        <button
            onClick={() => onItemClick?.(item)}
            className={`
                w-full text-left p-3 rounded-xl border transition-all duration-200
                hover:shadow-md hover:-translate-y-0.5
                ${urgency === 'overdue'
                    ? 'bg-rose-50 border-rose-200 hover:border-rose-300'
                    : urgency === 'today'
                        ? 'bg-amber-50 border-amber-200 hover:border-amber-300'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                }
            `}
        >
            <div className="flex items-center gap-3">
                <span className="text-lg">{getTypeIcon(item.type)}</span>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{item.title}</p>
                    {item.subtitle && (
                        <p className="text-xs text-slate-500 truncate">{item.subtitle}</p>
                    )}
                </div>
                <div className={`
                    text-xs font-semibold px-2 py-1 rounded-full
                    ${urgency === 'overdue'
                        ? 'bg-rose-100 text-rose-700'
                        : urgency === 'today'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                    }
                `}>
                    {getTimeRemaining(item.date)}
                </div>
            </div>
        </button>
    );

    const Section = ({ title, items, urgency, color, icon }) => {
        if (items.length === 0) return null;

        return (
            <div className="mb-4 last:mb-0">
                <div className={`flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wide ${color}`}>
                    <span>{icon}</span>
                    <span>{title}</span>
                    <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-600">{items.length}</span>
                </div>
                <div className="space-y-2">
                    {items.slice(0, 3).map((item, idx) => (
                        <DeadlineItem key={`${item.id}-${idx}`} item={item} urgency={urgency} />
                    ))}
                    {items.length > 3 && (
                        <p className="text-xs text-slate-500 text-center py-1">
                            +{items.length - 3} more
                        </p>
                    )}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-32 mb-4"></div>
                <div className="space-y-3">
                    <div className="h-14 bg-slate-100 rounded-xl"></div>
                    <div className="h-14 bg-slate-100 rounded-xl"></div>
                </div>
            </div>
        );
    }

    const totalCount = Object.values(grouped).reduce((sum, arr) => sum + arr.length, 0);

    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-slate-900">Upcoming Deadlines</h3>
                {totalCount > 0 && (
                    <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-medium">
                        {totalCount} pending
                    </span>
                )}
            </div>

            {totalCount === 0 ? (
                <div className="text-center py-6">
                    <div className="text-3xl mb-2">🎉</div>
                    <p className="text-sm text-slate-500">No upcoming deadlines</p>
                    <p className="text-xs text-slate-400 mt-1">You're all caught up!</p>
                </div>
            ) : (
                <div className="max-h-80 overflow-y-auto">
                    <Section
                        title="Overdue"
                        items={grouped.overdue}
                        urgency="overdue"
                        color="text-rose-600"
                        icon="🔴"
                    />
                    <Section
                        title="Today"
                        items={grouped.today}
                        urgency="today"
                        color="text-amber-600"
                        icon="⚡"
                    />
                    <Section
                        title="This Week"
                        items={grouped.thisWeek}
                        urgency="week"
                        color="text-blue-600"
                        icon="📆"
                    />
                    <Section
                        title="Upcoming"
                        items={grouped.upcoming}
                        urgency="later"
                        color="text-slate-500"
                        icon="📅"
                    />
                </div>
            )}
        </div>
    );
};

export default DeadlineCountdown;
